import { linkToSignOut } from "$routes/_lib/links";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@material-ui/core";
import { ExitToApp } from "@material-ui/icons";
import { Link } from "react-router-dom";
import { useBillingCodesEnabled } from "../_lib/billing/use-billing-codes-enabled";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../_lib/epocus-palette";
import { useRoleVariant } from "../_lib/epocus-role-variant";
import { epocusTheme } from "../_lib/epocus-theme-provider";
import { selectActiveProgram, useMyPrograms } from "../_lib/use-active-program";
import {
  useDefaultDevice,
  useSetDefaultDevice,
} from "../_lib/use-default-device";
import { useEnabledDevices } from "../_lib/use-enabled-devices";
import {
  useEpocusProgramIngressQuery,
  useMyEpocusUserQuery,
} from "./_lib/__generated__/epocus-settings-operations.gql";
import { AccountCard } from "./_lib/account-card";
import { AdministrationSection } from "./_lib/administration-section";
import { ConnectADeviceCard } from "./_lib/connect-a-device-card";
import { DemoDataCard } from "./_lib/demo-data-card";
import { IdentityHeader } from "./_lib/identity-header";
import { ProbeAssociationsCard } from "./_lib/probe-associations-card";
import { ProgramAeTitleCard } from "./_lib/program-ae-title-card";
import { ReminderEmailCard } from "./_lib/reminder-email-card";
import {
  ReviewerNotificationsCard,
  ReviewerPreferencesCard,
} from "./_lib/reviewer-preferences-cards";
import {
  SettingsCard,
  SettingsCardLabel,
  SettingsCardLabelRow,
  SettingsGroupHeading,
  SettingsScopeChip,
} from "./_lib/settings-card";
import { useCanManageProgramUsers } from "./_lib/use-can-manage-program-users";
import { useCanSeedDemoData } from "./_lib/use-can-seed-demo-data";

const NO_DEFAULT_VALUE = "__none__";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const Footer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 8,
});

const DeviceDefaultCard: FC = () => {
  const defaultDeviceId = useDefaultDevice();
  const setDefaultDevice = useSetDefaultDevice();
  const deviceOptions = useEnabledDevices();

  // A stale stored id has no matching MenuItem, which MUI renders as a blank
  // Select; show "No default" instead so the user can re-pick.
  const selectedValue =
    defaultDeviceId &&
    deviceOptions.some((device) => device.id === defaultDeviceId)
      ? defaultDeviceId
      : NO_DEFAULT_VALUE;

  return (
    <SettingsCard>
      <SettingsCardLabelRow>
        <SettingsCardLabel>Default device</SettingsCardLabel>
        <SettingsScopeChip>This browser only</SettingsScopeChip>
      </SettingsCardLabelRow>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        Pre-fills the device on New Encounter so you don't have to pick it each
        time.
      </Typography>
      <Select
        fullWidth
        variant="outlined"
        margin="dense"
        value={selectedValue}
        onChange={(e) => {
          const value = String(e.target.value);
          setDefaultDevice(value === NO_DEFAULT_VALUE ? null : value);
        }}
        inputProps={{ "aria-label": "Default device" }}
      >
        <MenuItem value={NO_DEFAULT_VALUE}>No default</MenuItem>
        {deviceOptions.map((device) => (
          <MenuItem key={device.id} value={device.id}>
            {device.label}
          </MenuItem>
        ))}
      </Select>
    </SettingsCard>
  );
};

export const EpocusSettingsPage: FC = () => {
  const { data, loading } = useMyEpocusUserQuery();
  const myEpocusUser = data?.myEpocusUser;
  const canManageUsers = useCanManageProgramUsers();
  const canSeedDemoData = useCanSeedDemoData();
  const isReviewer = useRoleVariant() === "reviewer";
  const billingCodesEnabled = useBillingCodesEnabled();

  const { programs } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const aeTitle = program?.dimseAeTitle;
  const showProgramGroup = !!aeTitle || canManageUsers;

  const { data: ingressData, loading: ingressLoading } =
    useEpocusProgramIngressQuery({
      variables: { programId: program?.id ?? "" },
      skip: !canManageUsers || !program,
    });
  const ingress = ingressData?.epocusProgramIngress;

  return (
    <PageRoot>
      <Box>
        <Typography
          variant="h5"
          style={{
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            fontSize: rem(24),
            lineHeight: 1.2,
          }}
        >
          Settings
        </Typography>
      </Box>

      <IdentityHeader
        firstName={myEpocusUser?.firstName}
        lastName={myEpocusUser?.lastName}
        programName={program?.name}
      />

      <SettingsGroupHeading scope="Follows you on any device">
        You
      </SettingsGroupHeading>
      {loading && !myEpocusUser ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading your details" />
        </LoadingRow>
      ) : myEpocusUser ? (
        <>
          <AccountCard
            firstName={myEpocusUser.firstName}
            lastName={myEpocusUser.lastName}
            email={myEpocusUser.email}
            telephone={myEpocusUser.telephone}
            npi={myEpocusUser.npi}
            memberships={myEpocusUser.memberships}
          />
          {billingCodesEnabled && (
            <ReminderEmailCard
              reminderEmailEnabled={myEpocusUser.reminderEmailEnabled}
              reminderEmailTime={myEpocusUser.reminderEmailTime}
            />
          )}
        </>
      ) : (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            We couldn't load your account details. Please try again later.
          </Typography>
        </SettingsCard>
      )}

      {isReviewer ? (
        <>
          <SettingsGroupHeading scope="Stored on this device">
            Your preferences
          </SettingsGroupHeading>
          <ReviewerNotificationsCard />
          <ReviewerPreferencesCard />
        </>
      ) : (
        <>
          <SettingsGroupHeading scope="What you scan with">
            Your setup
          </SettingsGroupHeading>
          <ProbeAssociationsCard />
          <DeviceDefaultCard />
        </>
      )}

      {showProgramGroup && (
        <>
          <SettingsGroupHeading scope={program?.name ?? undefined}>
            Your program
          </SettingsGroupHeading>
          {ingress ? (
            <ConnectADeviceCard
              aeTitle={ingress.aeTitle}
              endpoints={ingress.endpoints}
            />
          ) : (
            !ingressLoading &&
            aeTitle && <ProgramAeTitleCard aeTitle={aeTitle} />
          )}
          {canManageUsers && <AdministrationSection />}
        </>
      )}

      <Footer>
        <Button
          component={Link}
          to={linkToSignOut()}
          variant="outlined"
          color="primary"
          startIcon={<ExitToApp />}
          style={{ alignSelf: "flex-start" }}
        >
          Log out
        </Button>
        {canSeedDemoData && <DemoDataCard />}
      </Footer>
    </PageRoot>
  );
};
